import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useCobrowseStore } from '../stores/cobrowseStore';
import { useGlobalStore } from '../stores/globalStore';
import { useConnectionStore } from '../stores/connectionStore';
import { apiHeaders } from '../api/client';

interface CobrowseSDKInstance {
  /** Resolves false if the customer declined the consent prompt. */
  start(): Promise<boolean>;
  stop(): void;
  getSessionCode(): string | null;
  setAuthToken(token: string): void;
  setUser(user: CobrowseUser | null): void;
  /** Expand the chat if the customer has shrunk it. Optional: older SDK builds lack it. */
  openChat?(): void;
  /** Minimise the chat. Optional, as openChat. */
  minimizeChat?(): void;
  /** Take the minimised chat off the page into our own button (null: back to a bubble). */
  dockChat?(dock: ChatDock | null): void;
}

/** Where the chat docks: how far its open panel sits above the bottom of the window. */
export interface ChatDock {
  bottom: number;
}

/**
 * Who the relay should say is on the other end.
 *
 * Separate from the auth token on purpose. The relay's Keycloak realm is
 * techlog and ours is Dietech, so a Dietech token cannot be verified there —
 * but *identifying* the customer never needed a token. Without this the agent
 * console shows "Anonymous", and the after-hours callback form asks a
 * signed-in user to type their own name back to us.
 *
 * The relay marks anything sourced this way `unverified`, which is the honest
 * label: it is the app's word for who this is, not a proven claim.
 */
interface CobrowseUser {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  /** The facility they are working in *right now* — this changes mid-session. */
  facility: string;
  /**
   * Same value as `facility`, mapped onto the relay's "Company" field on the
   * after-hours callback form. There is no separate company — the facility the
   * user is in (e.g. "PSI Touch") IS who they are with.
   */
  company: string;
  /** Which database connection, so support knows where to look. */
  connection: string;
}

interface CobrowseSDKConstructor {
  new (options: Record<string, unknown>): CobrowseSDKInstance;
}

// Dynamically loaded SDK class reference (module-level singleton)
let CobrowseSDKClass: CobrowseSDKConstructor | null = null;

// A page has exactly one cobrowse session, but several components render this
// hook (top toolbar icon, bottom toolbar button). Per-component refs would give
// each one its own SDK and its own connection, so both the instance and its
// connect watchdog live at module scope, next to the shared zustand store.
let sdkInstance: CobrowseSDKInstance | null = null;
let connectTimer: ReturnType<typeof setTimeout> | null = null;

// A start that is under way. The SDK bundle is imported before the instance
// is stored, and on a page load with a saved session every toolbar rendering
// this hook calls startSession inside that gap — each found no instance and
// built its own, so two sockets rejoined the same session and the first stayed
// connected, unseen, after it ended. Callers in that gap now share this one.
let starting: Promise<void> | null = null;

/**
 * Who can dock the chat, first one wins. Selective Touch is a full-screen
 * overlay with the status strip still mounted (and still "on screen" by its
 * own measure) underneath, so while it is up its header button has the chat.
 */
const DOCK_OWNERS = ['touch', 'strip'] as const;
export type ChatDockOwner = (typeof DOCK_OWNERS)[number];

// Set by each support button while it is on screen; see dockSupportChat. Kept
// here so an SDK built later still gets it.
const chatDocks: Partial<Record<ChatDockOwner, ChatDock>> = {};

function currentChatDock(): ChatDock | null {
  for (const owner of DOCK_OWNERS) {
    const dock = chatDocks[owner];
    if (dock) return dock;
  }
  return null;
}

/**
 * Dock the support chat into a support button, or (null) give it up.
 *
 * The chat floated over the page, and minimised it was still a bubble over the
 * page — on top of the status strip's own Support Chat button. Customers asked
 * for it to go down into the toolbar: docked, minimising takes it off the page,
 * the owner's button stands for it (with an unread count), and the open panel
 * sits above the strip instead of across it. Selective Touch does the same with
 * its header button, keeping the panel clear of its footer.
 *
 * Only for a button that is really on screen: a docked chat that is minimised
 * can be brought back by nothing else on the page that shows its messages.
 */
export function dockSupportChat(owner: ChatDockOwner, dock: ChatDock | null) {
  if (dock) chatDocks[owner] = dock;
  else delete chatDocks[owner];
  sdkInstance?.dockChat?.(currentChatDock());
}

/**
 * The signed-in user's Dietech token, or null on the landing page.
 *
 * From apiHeaders rather than the hook's getAccessToken: the SDK instance is
 * shared, and whichever component built it may be one that passes no token.
 */
async function currentDietechToken(): Promise<string | null> {
  const header = apiHeaders().Authorization;
  return header ? header.replace(/^Bearer\s+/i, '') : null;
}

function clearConnectTimer() {
  if (connectTimer) {
    clearTimeout(connectTimer);
    connectTimer = null;
  }
}

/**
 * Resolves the URL to the cobrowse ESM SDK bundle.
 *
 * In dev: the Vite proxy rewrites /cobrowse/* to the relay server.
 * In prod: the relay server is at VITE_COBROWSE_HTTP_URL (or same origin /cobrowse/).
 */
function getSdkUrl(): string {
  // If a dedicated HTTP URL is set for the relay, use it directly
  const httpBase = import.meta.env.VITE_COBROWSE_HTTP_URL;
  if (httpBase) {
    return `${httpBase}/sdk/cobrowse-sdk.esm.js`;
  }
  // Otherwise go through the Vite proxy (dev) or relative path (prod)
  return '/cobrowse/sdk/cobrowse-sdk.esm.js';
}

/**
 * How long to wait for the relay to hand back a session code *after* the
 * customer has consented. This never covers the consent prompt itself — the
 * customer may take as long as they like to answer, and timing that out used
 * to abandon a session that was about to connect.
 */
const CONNECT_TIMEOUT_MS = 20000;

/**
 * Key the SDK uses to remember an in-progress session (see customer-sdk).
 * Reading it lets the app tell "a session is running" from "no session" after
 * a full page load.
 */
const SDK_SESSION_KEY = 'cobrowse_session';

function hasSavedSdkSession(): boolean {
  try {
    return Boolean(sessionStorage.getItem(SDK_SESSION_KEY));
  } catch {
    return false;
  }
}

/** Mask sensitive fields in the PrimeManager app.
 *  Any element matching these selectors will have its text replaced with
 *  asterisks in the cobrowse recording. Add data-cobrowse-mask to any
 *  element in the app to mask it dynamically. */
const MASKED_SELECTORS = [
  'input[type="password"]',
  '[data-cobrowse-mask]',
  '.cobrowse-mask',
  '.sensitive-data',
  // PrimeManager-specific sensitive fields
  'input[name*="ssn" i]',
  'input[name*="social" i]',
  'input[name*="dob" i]',
  'input[name*="dateOfBirth" i]',
  'input[name*="birthDate" i]',
  'input[name*="medicaid" i]',
  'input[name*="medicare" i]',
  'input[name*="insurance" i]',
  'input[name*="cardNumber" i]',
  'input[name*="accountNumber" i]',
  '[data-field="ssn"]',
  '[data-field="dob"]',
  '[data-field="dateOfBirth"]',
  '.resident-ssn',
  '.resident-dob',
  '.phi-field',
] as const;

/**
 * Hook that manages a cobrowse session for the current user.
 * Follows the same lifecycle pattern as useKitchenSignalR.
 *
 * @param _getAccessToken - no longer read. It fed the effect that pushed the
 *   token onto the relay connection (see below); the office-hours lookup uses
 *   the page's current token instead, since a getter captured by whichever
 *   component built the shared SDK goes stale when the token refreshes. Kept
 *   so the callers do not change.
 * @param enabled - whether the hook should be active (default true)
 */
export function useCobrowse(
  _getAccessToken: () => Promise<string | null>,
  enabled: boolean = true,
) {
  // Use individual selectors from the store to avoid re-render loops
  const status = useCobrowseStore((s) => s.status);
  const sessionCode = useCobrowseStore((s) => s.sessionCode);
  const agentCount = useCobrowseStore((s) => s.agentCount);
  const error = useCobrowseStore((s) => s.error);
  const chatOpen = useCobrowseStore((s) => s.chatOpen);
  const chatUnread = useCobrowseStore((s) => s.chatUnread);

  // Read fields individually: a selector that builds an object returns a new
  // reference every render, and zustand would re-render on it forever.
  const userFirstName = useGlobalStore((s) => s.userFirstName);
  const userLastName = useGlobalStore((s) => s.userLastName);
  const userEmail = useGlobalStore((s) => s.userEmail);
  const facilityName = useGlobalStore((s) => s.facilityName);
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);

  const cobrowseUser = useMemo<CobrowseUser>(() => ({
    firstName: userFirstName || '',
    lastName: userLastName || '',
    name: [userFirstName, userLastName].filter(Boolean).join(' '),
    email: userEmail || '',
    facility: facilityName || '',
    company: facilityName || '',
    connection: selectedConnection?.facilityName || '',
  }), [userFirstName, userLastName, userEmail, facilityName, selectedConnection]);

  // Held in a ref so startSession does not have to list identity in its deps.
  // It is a stable callback by design — rebuilding it whenever someone switches
  // facility would fight an in-flight connection.
  const identityRef = useRef(cobrowseUser);
  identityRef.current = cobrowseUser;

  const serverUrl = useMemo(() => {
    // SDK expects a WebSocket URL — it creates `new WebSocket(serverUrl)`
    const wsUrl = import.meta.env.VITE_COBROWSE_URL;
    if (wsUrl) return wsUrl as string;
    // Auto-derive from page protocol
    const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProto}://${window.location.hostname}:4738`;
  }, []);

  const startSessionNow = useCallback(async () => {
    const store = useCobrowseStore.getState();

    // Restarting an SDK we already built (e.g. after the customer ended a
    // session without leaving the page).
    if (sdkInstance) {
      try {
        store.setStatus('requesting-consent');
        const consented = await sdkInstance.start();
        if (!consented) {
          useCobrowseStore.getState().reset();
        }
      } catch (err) {
        store.setError(err instanceof Error ? err.message : 'Failed to start cobrowse session');
      }
      return;
    }

    try {
      store.setStatus('connecting');

      // Dynamically load the ESM SDK bundle if not cached
      if (!CobrowseSDKClass) {
        const sdkUrl = getSdkUrl();
        const mod = await import(/* @vite-ignore */ sdkUrl);
        CobrowseSDKClass = mod.CobrowseSDK as CobrowseSDKConstructor;
      }

      if (!CobrowseSDKClass) {
        throw new Error('CobrowseSDK not found. Is the cobrowse relay server running?');
      }

      // No auth token: the relay authenticates agents against the techlog realm,
      // and a Dietech token would fail JWKS validation there. Identity is a
      // separate matter — `user` carries who this is without claiming to prove
      // it, which is what the agent console and the after-hours form need.
      const sdk = new CobrowseSDKClass({
        serverUrl,
        user: identityRef.current,
        // The one place a Dietech token does belong. Whether support is open
        // comes from our BusinessHours API, which stopped answering anonymous
        // callers — so every customer was told support was open, at midnight
        // too. The SDK sends this only with its office-hours request, which the
        // relay passes on to that API; it never goes on the relay connection.
        // Null on the landing page, where the relay asks on its own.
        officeHoursToken: currentDietechToken,
        maskedSelectors: [...MASKED_SELECTORS],
        showSessionWidget: false, // PrimeManager's CobrowseButton handles the UI
        onSessionStart: (code: string) => {
          clearConnectTimer();
          const s = useCobrowseStore.getState();
          s.setSessionCode(code);
          s.setStatus('active');
        },
        onSessionEnd: () => {
          clearConnectTimer();
          useCobrowseStore.getState().reset();
        },
        onConsentDenied: () => {
          clearConnectTimer();
          sdkInstance = null;
          useCobrowseStore.getState().reset();
        },
        onAgentJoin: () => {
          const s = useCobrowseStore.getState();
          s.setAgentCount(s.agentCount + 1);
        },
        onAgentLeave: () => {
          const s = useCobrowseStore.getState();
          s.setAgentCount(Math.max(0, s.agentCount - 1));
        },
        onChatChange: ({ open, unread }: { open: boolean; unread: number }) => {
          useCobrowseStore.getState().setChat(open, unread);
        },
      });

      sdkInstance = sdk;
      const dock = currentChatDock();
      if (dock) sdk.dockChat?.(dock);
      store.setStatus('requesting-consent');

      // Do NOT race the consent prompt: it has no time limit, and a customer
      // who reads it carefully would otherwise get an error while a real
      // session connected in the background with nothing left holding it.
      const consented = await sdk.start();
      if (!consented) {
        clearConnectTimer();
        sdkInstance = null;
        useCobrowseStore.getState().reset();
        return;
      }

      // Consent given — now the relay has a bounded window to hand back a code.
      clearConnectTimer();
      connectTimer = setTimeout(() => {
        connectTimer = null;
        if (useCobrowseStore.getState().status === 'active') return;
        // Tear the SDK down rather than leaving it connecting unobserved.
        try { sdkInstance?.stop(); } catch { /* already gone */ }
        sdkInstance = null;
        useCobrowseStore
          .getState()
          .setError('Connection timed out. The cobrowse relay server may not be available.');
      }, CONNECT_TIMEOUT_MS);
    } catch (err) {
      clearConnectTimer();
      try { sdkInstance?.stop(); } catch { /* never started */ }
      sdkInstance = null;
      store.setError(err instanceof Error ? err.message : 'Failed to start cobrowse session');
    }
  }, [serverUrl]);

  /** Start a session, or join the start already under way; see `starting`. */
  const startSession = useCallback(async () => {
    if (starting) return starting;
    starting = startSessionNow().finally(() => { starting = null; });
    return starting;
  }, [startSessionNow]);

  /**
   * Bring the support chat back, for a session that is running.
   *
   * What the toolbar buttons do while a session is active. They used to end
   * it: a customer who had shrunk the chat and reached for the Support button
   * to get it back ended their session in one click, and the next click
   * started a new one under a new code. Ending is in the chat itself, which
   * asks first.
   */
  const openChat = useCallback(() => {
    sdkInstance?.openChat?.();
  }, []);

  /** Minimise the support chat; with the chat docked, into the status strip. */
  const minimizeChat = useCallback(() => {
    sdkInstance?.minimizeChat?.();
  }, []);

  const stopSession = useCallback(() => {
    clearConnectTimer();
    if (sdkInstance) {
      sdkInstance.stop();
      sdkInstance = null;
    }
    useCobrowseStore.getState().reset();
  }, []);

  // Resume a session that survived a full page load.
  //
  // Signing in leaves dietechonline.com entirely for the Keycloak origin, where
  // no SDK exists, and returning here is a fresh page load with nothing running
  // — the agent kept watching a session that had stopped sending events. The
  // SDK stores its session in sessionStorage, which survives that round trip in
  // the same tab, and start() takes the rejoin path when it finds one, so this
  // reconnects without prompting the customer for consent again.
  useEffect(() => {
    if (!enabled) return;
    if (sdkInstance) return;
    if (!hasSavedSdkSession()) return;
    startSession();
    // Run once on mount: startSession is stable for the life of the page and
    // re-running on every change would fight an in-flight connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Push identity changes into a running session.
  //
  // Facility and connection are not fixed for the length of a call: a user can
  // hold several facilities and switch between them while an agent is watching.
  // Without this the console keeps showing the one they started on, which is
  // the screen support is *not* looking at.
  useEffect(() => {
    if (!enabled || !sdkInstance) return;
    sdkInstance.setUser(cobrowseUser);
  }, [cobrowseUser, enabled]);

  // No effect pushing the token into the SDK's authToken. One did, whenever
  // the token refreshed — contradicting the decision above to send none. The
  // relay could not verify a Dietech token, refused every reconnect that
  // carried one, and the SDK gave up after six: a session about an hour old
  // ended on the first Wi-Fi blip. The office-hours lookup, which is what
  // wants the token, reads it through officeHoursToken instead.

  // Deliberately no stop-on-unmount: the session belongs to the page, not to
  // whichever toolbar rendered this hook. Tearing it down here ended live
  // support sessions whenever the layout remounted. The customer ends a session
  // from the chat's End Session (or stopSession()), and closing the tab drops
  // the socket anyway.

  return {
    status,
    sessionCode,
    agentCount,
    error,
    chatOpen,
    chatUnread,
    startSession,
    stopSession,
    openChat,
    minimizeChat,
  };
}
